/**
 * DOM节点操作工具函数
 * @author luoying
 * @since 17/06/20
 */
/**
 * 获取指定DOM节点相对于指定容器的绝对坐标
 * 当不指定相对容器时，则相对于文档根节点
 * @param {Node} node DOM节点
 * @param {(Node | null)} [container] 相对容器
 * @return {{left: number; top: number} | null} 返回绝对坐标，{ left, top }
 */
export function offset(node, container) {
    if (!(node instanceof HTMLElement))
        return null;
    let top = 0;
    let left = 0;
    let parent = node;
    do {
        top += parent.offsetTop;
        left += parent.offsetLeft;
        parent = parent.offsetParent;
    } while (parent);
    // 减去容器的绝对距离
    if (container) {
        const containerOffset = offset(container);
        if (containerOffset) {
            left -= containerOffset.left;
            top -= containerOffset.top;
        }
    }
    return { top, left };
}
/**
 * 检测节点是否在指定的节点列表中
 * @param {Node} node 节点
 * @param {NodeListOf<Node>} nodeList 节点列表
 * @returns {boolean}
 */
export function contains(node, nodeList) {
    for (let i = 0; i < nodeList.length; i++) {
        const item = nodeList.item(i);
        if (item === node)
            return true;
    }
    return false;
}
/**
 * 获取指定DOM节点的指定父节点
 * 当不指定父节点选择器，则返回直系父节点
 * @param  {Node} node     DOM节点
 * @param  {string} [selector]    父节点选择器
 * @return {Node | null}          返回查询到的父节点
 */
export function parent(node, selector) {
    if (!selector)
        return node.parentNode;
    const nodes = document.querySelectorAll(selector);
    if (!nodes.length)
        return null;
    let p = node;
    do {
        p = p.parentNode;
    } while (p && !contains(p, nodes));
    return p;
}
/**
 * 检测是否是指定节点的后代节点
 * @param {Node} node 后代节点
 * @param {string | Node} selector 指定节点
 * @returns {boolean}
 */
export function isDescendant(node, selector) {
    if (typeof selector === 'string') {
        const nodes = document.querySelectorAll(selector);
        if (!nodes.length)
            return false;
        let parent = node;
        while (parent) {
            if (contains(parent, nodes))
                return true;
            parent = parent.parentNode;
        }
        return false;
    }
    let parent = node;
    while (parent) {
        if (parent === selector)
            return true;
        parent = parent.parentNode;
    }
    return false;
}
/**
 * @alias isDescendant
 * @deprecated 已废弃，请使用`isDescendant`
 */
export const targetIsDescendant = isDescendant;
/**
 * 检查一个DOM节点是否在当前视口内
 * @param   {Node} node      DOM节点
 * @param   {Element} container 容器
 * @param   {number} threshhold 阈值
 * @returns {boolean}    返回检查结果
 */
export function checkInViewport(node, container, threshhold) {
    if (!(node instanceof HTMLElement))
        return false;
    const doc = container || document.scrollingElement || document.documentElement;
    const offset = threshhold || 0;
    const docViewTop = Math.max(doc.scrollTop - offset, 0);
    const docViewBottom = docViewTop + document.body.getBoundingClientRect().height + offset * 2;
    const nodeTop = node.offsetTop;
    const nodeBottom = nodeTop + node.getBoundingClientRect().height;
    return nodeBottom <= docViewBottom && docViewTop <= nodeTop;
}
/**
 * 检测DOM节点是否是可编辑节点，例如：input/textareat/div[contenteditable]
 * @param {Node} node
 * @returns {boolean}
 */
export function isEditableNode(node) {
    const inputableNodes = ['INPUT', 'TEXTAREA'];
    if (!(node instanceof HTMLElement))
        return false;
    const editable = node.contentEditable;
    return inputableNodes.includes(node.nodeName) || editable === 'true';
}
/**
 * 剥离DOM节点上的所有`font-family`样式规则
 * @param node DOM节点
 * @param recursive 是否需要递归遍历所有子节点
 * @returns
 */
export function stripFontFamily(node, recursive = false) {
    if (!(node instanceof HTMLElement))
        return;
    if (node.style.fontFamily)
        node.style.fontFamily = '';
    if (recursive && node.children.length) {
        for (let i = 0; i < node.children.length; i++) {
            const child = node.children.item(i);
            child && stripFontFamily(child, recursive);
        }
    }
}
/**
 * 剥离html片段上的所有`font-family`样式属性
 * @param html html片段
 * @param recursive 是否需要递归遍历所有子节点
 * @returns {string} 返回被剥离了`font-family`的新html片段
 */
export function stripFontFamilyForHtml(html, recursive = false) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    for (let i = 0; i < tmp.children.length; i++) {
        const node = tmp.children.item(i);
        node && stripFontFamily(node, recursive);
    }
    return tmp.innerHTML;
}
//# sourceMappingURL=node.js.map